import React, { useState, useEffect } from 'react'

import { useParams, useHistory } from 'react-router-dom'

import { toast } from 'react-toastify'

import { I18n } from 'aws-amplify'
import Checkout from '../../../../../presentation/pages/common/checkout'

import { useMe } from '../../../../../presentation/contexts/me'

import { makePlans } from '../../../../usecases/plans/remote-plans-factory'
import { makePayments } from '../../../../usecases/payments/remote-payments-factory'

import bancos from '../../../../../presentation/assets/png/bancos.png'
import isHttpError from '../../../../../presentation/helpers/helper-http-error'
import IPlan from '../../../../../presentation/interfaces/plan'
import { PaymentType } from '../../../../../presentation/enums/payment-type.enum'
import { PaymentOptions } from '../../../../../presentation/helpers'

const MakeCheckout = () => {
  document.title = 'BuscarMédicos - Pagamento'

  const [plan, setPlan] = useState<IPlan>({
    id: 0,
    period: '',
    values: 0,
    planTitle: '',
    enabled: false,
    createdAt: new Date(),
    updatedAt: new Date(),
    type: '',
    promotionalPrice: 0,
  })
  console.log('plan', plan)

  const [errorMessage, setErrorMessage] = useState<string | null>('')

  const { type, id } = useParams<{
    type: string
    id: string
  }>()

  const { me } = useMe()

  const history = useHistory()

  const [paymentMethod, setPaymentMethod] = useState<PaymentType>(
    PaymentType.CREDIT,
  )

  useEffect(() => {
    const fetchPlan = async () => {
      const data = await makePlans().loadById(Number(id))
      setPlan(data)
    }

    fetchPlan()
  }, [])

  const maxInstallments = () => {
    switch (plan?.period) {
      case 'Trimestral':
        return 3
      case 'Semestral':
        return 6
      case 'Anual':
        return 12
      default:
        return 3
    }
  }

  const installmentsOptionsCredit = Array.from(
    { length: maxInstallments() },
    (_, i) => ({
      value: String(i + 1),
      label: `${i + 1}x de R$ ${
        plan?.promotionalPrice !== null && plan?.promotionalPrice !== 0
          ? (plan?.promotionalPrice / (i + 1)).toFixed(2)
          : (plan?.values / (i + 1)).toFixed(2)
      }`,
    }),
  )

  const installmentsOptionsDebit = [{
    value: '1',
    label: `1x de R$ ${plan?.promotionalPrice !== null && plan?.promotionalPrice !== 0 ? plan?.promotionalPrice.toFixed(2) : plan?.values.toFixed(2)}`,
  }]

  const installmentsOptions = paymentMethod === PaymentType.DEBIT
    ? installmentsOptionsDebit
    : installmentsOptionsCredit

  const validateCardBrand = (cardNumber: string): string => {
    const firstDigit = cardNumber.charAt(0)

    if (firstDigit === '3') {
      return 'Diners Club / American Express'
    }
    if (firstDigit === '4') {
      return 'Visa'
    }
    if (firstDigit === '5') {
      const secondDigit = cardNumber.charAt(1)
      if (['1', '2', '3', '4', '5'].includes(secondDigit)) {
        return 'Mastercard'
      }
    } else if (firstDigit === '6') {
      return 'Discover'
    }

    return 'Unknown'
  }

  const handlePaymentMethodChange = (value: string) => {
    setPaymentMethod(value as PaymentType)
  }

  const handleCardPayment = (values: any): any => {
    const cardBrand = validateCardBrand(values.cardNumber)
    if (cardBrand === 'Unknown') {
      setErrorMessage('Bandeira do cartão não suportada.')
      return null
    }

    const [month, year] = values.data.split('/')

    const cleanedHolderDocument = values.holderDocument.replace(/[^\d]/g, '')

    const payload = {
      email: me?.email,
      planId: plan?.id,
      paymentMethod: values.paymentMethod,
      card: {
        holderDocument: cleanedHolderDocument,
        holderName: values.name,
        brand: cardBrand,
        number: values.cardNumber,
        expMonth: Number(month),
        expYear: Number(year),
        cvv: values.cvv,
        name: values.name,
        installments: JSON.parse(values.parcela),
      },
    }

    return payload
  }

  const handleBoletoPayment = (values: any): any => {
    const payload = {
      email: me?.email,
      planId: plan?.id,
      paymentMethod: values.paymentMethod,
    }

    return payload
  }

  const handleSubmit = async (values: any): Promise<void> => {
    let payload = null
    if (values.paymentMethod === PaymentType.BOLETO) {
      payload = handleBoletoPayment(values)
    } else {
      payload = handleCardPayment(values)
    }

    try {
      if (type === 'CONTRATANTE') {
        await makePayments('contractors').post(payload, me?.id as number)
      } else if (type === 'MEDICO') {
        await makePayments('doctor').post(payload, me?.id as number)
      }

      const userType = type === 'CONTRATANTE' ? 'contractors' : 'doctors'

      history.push(`/${userType}/profile`)
    } catch (error) {
      const httpError = isHttpError(error)

      if (httpError) {
        toast.error(I18n.get(httpError.message))
      } else {
        throw error
      }
    }
  }

  const dataForm = [
    {
      items: [
        {
          component: 'InputGroup',
          style: {
            alignItems: 'center',
          },
          inputs: [
            {
              component: 'Title',
              subtitle: 'Forma de pagamento',
              textColorBlack: true,
              images: bancos,
            },
            {
              component: 'InputRadio',
              name: 'paymentMethod',
              title: 'Forma de pagamento',
              defaultValue: PaymentType.CREDIT,
              onChange: handlePaymentMethodChange,
              options: PaymentOptions,
            },
          ],
        },
        {
          component: 'InputMask',
          mask: 'CREDIT_CARD_NUMBER',
          name: 'cardNumber',
          label: 'Número do cartão',
          title: 'Número do cartão',
          type: 'tel',
          value: '',
          required: true,
          placeholder: 'Digite aqui',
          styleContainer: {
            display: paymentMethod === PaymentType.BOLETO ? 'none' : 'flex',
          },
          validations:
            paymentMethod !== PaymentType.BOLETO ? 'inputRequired' : '',
        },
        {
          component: 'InputGroup',
          style: {
            display: paymentMethod === PaymentType.BOLETO ? 'none' : 'flex',
          },
          inputs: [
            {
              component: 'InputMask',
              mask: 'EXPIRATION_DATE',
              name: 'data',
              label: 'Data de validade',
              title: 'Data de validade',
              type: 'tel',
              value: '',
              required: true,
              placeholder: 'Digite aqui',
              validations:
                paymentMethod !== PaymentType.BOLETO ? 'inputRequired' : '',
            },
            {
              name: 'cvv',
              component: 'Input',
              value: '',
              placeholder: 'Digite aqui',
              type: 'number',
              title: 'Código de segurança',
              label: 'Código de segurança',
              required: true,
              validations:
                paymentMethod !== PaymentType.BOLETO ? 'inputRequired' : '',
            },
          ],
        },
        {
          name: 'name',
          component: 'Input',
          value: '',
          placeholder: 'Digite aqui',

          type: 'text',
          title: 'Nome no cartão',
          label: 'Nome no cartão',
          required: true,
          styleContainer: {
            display: paymentMethod === PaymentType.BOLETO ? 'none' : 'flex',
          },
          validations:
            paymentMethod !== PaymentType.BOLETO ? 'inputRequired' : '',
        },
        {
          component: 'InputMask',
          mask: 'CPF',
          name: 'holderDocument',
          label: 'CPF do titular',
          title: 'CPF do titular',
          type: 'tel',
          value: '',
          required: true,
          placeholder: 'Digite aqui',
          styleContainer: {
            display: paymentMethod === PaymentType.BOLETO ? 'none' : 'flex',
          },
          validations:
            paymentMethod !== PaymentType.BOLETO ? 'inputRequired' : '',
        },
        {
          name: 'parcela',
          component: 'Select',
          value: '',
          title: 'Parcela',
          label: 'Digite o valor',
          required: true,
          options: installmentsOptions.map((item: any) => ({
            value: item.value,
            label: item.label,
          })),
          hasPlusButton: false,
          styleContainer: {
            display: paymentMethod === PaymentType.BOLETO ? 'none' : 'flex',
          },
        },
        {
          name: 'button',
          component: 'Button',
          title: 'Concluir',
          submittingText: 'Carregando...',
          color: true,
          style: { marginBottom: '16px' },
        },
      ],
    },
  ]

  const checkoutProps = {
    dataForm,
    onSubmit: handleSubmit,
    errorMessage,
    plan,
  }

  return <Checkout {...checkoutProps} />
}

export default MakeCheckout
